import { AccentColor } from "@/lib/colors";

/**
 * The shape of a job opening on the careers pages, and the copy every job page
 * shares.
 *
 * The openings themselves live in Firestore (the `website` database's `jobs`
 * collection) and are edited in the portal's Website → Open positions tab by
 * users with the Website editor role. The site reads the published ones at
 * build time (`loadJobs.ts`), so an edit reaches the site when someone presses
 * Publish there, which rebuilds and deploys it. Each one appears in the
 * careers listing and gets its own page at `/careers/<slug>` with a built-in
 * application form.
 *
 * The fields mirror `JobFields` in `backend/src/portal/websiteJobs.shared.ts`,
 * the rules the portal edits under: a field added there must be added here.
 *
 * Note: job content is written in a single language, the one the role is
 * advertised in, and is shared across all locales (the surrounding page
 * chrome — buttons, section titles — is still translated).
 */

export interface JobDetailItem {
  /** Short bold lead-in, e.g. "Follow-up calls". */
  title: string;
  body: string;
}

export interface JobOpening {
  /** URL slug — must be unique and kebab-case. */
  slug: string;
  title: string;
  /** One-line teaser shown in the careers listing. */
  summary: string;
  /** Location shown in the listing, e.g. "Gothenburg / Hybrid". */
  location: string;
  /** Hero subtitle on the job detail page. */
  subtitle: string;
  department: string;
  language: string;
  /** Employment type, e.g. "Full-time". */
  employment: string;
  remote: string;
  /** Accent colour for the detail page divider/badges. */
  accent: AccentColor;
  /** Role description paragraph(s). */
  roleDescription: string;
  responsibilities: JobDetailItem[];
  requirements: JobDetailItem[];
  benefits: JobDetailItem[];
  /** Optional override of the shared "Why Scaffcalc?" blurb. */
  why?: string;
  hiringManager: { name: string; role: string; initials: string };
  /** Always true here: only published jobs are read. */
  published?: boolean;
}

/** Shared "About Scaffcalc" blurb reused across job pages. */
export const COMPANY_ABOUT =
  "We are digitizing the entire scaffolding process. With our cloud-based 3D platform, contractors can dimension, visualize, and document scaffolds in minutes instead of days — safer, smarter, and more profitable.";

/** Shared "Why Scaffcalc?" blurb; individual roles may override via `why`. */
export const COMPANY_WHY =
  "This role offers an exciting opportunity to join a fast-growing company and directly contribute to Scaffcalc's international growth. You'll work alongside a tight, prestige-less team that loves seeing each other succeed.";
